// Shell de layout del panel: sidebar de navegación + topbar.
//
// Responsive: en móvil (<720px) el sidebar se oculta y se muestra un botón
// hamburguesa en el topbar. Al pulsarlo, el sidebar se desliza desde la
// izquierda con un backdrop que lo cierra al tocarlo.

import {
  iconoDashboard,
  iconoProductos,
  iconoVariantes,
  iconoCupones,
  iconoCategorias,
  iconoPagina,
  iconoApariencia,
  iconoEmpresa,
  iconoCandado,
  iconoUsuario,
} from '@plataforma/ui';
import { tieneFeature } from '@plataforma/utils';

import { cerrarSesion, obtenerEstadoSesion } from '../estado-sesion.js';
import { empresaRequiereRegularizacion } from '../guardas.js';

const MODULOS_BASE = [
  { ruta: '/', etiqueta: 'Dashboard', icono: iconoDashboard },
  { ruta: '/productos', etiqueta: 'Productos', icono: iconoProductos },
  { ruta: '/categorias', etiqueta: 'Categorías', icono: iconoCategorias },
  { ruta: '/pagina-principal', etiqueta: 'Página principal', icono: iconoPagina },
  { ruta: '/personalizar-tienda', etiqueta: 'Personalizar tienda', icono: iconoApariencia },
  { ruta: '/empresa', etiqueta: 'Mi empresa', icono: iconoEmpresa },
  { ruta: '/pagos', etiqueta: 'Pagos', icono: iconoCandado },
];

function modulosDisponibles() {
  const empresa = obtenerEstadoSesion().empresa ?? {};

  if (empresaRequiereRegularizacion(empresa)) {
    return MODULOS_BASE.filter((m) => m.ruta === '/pagos');
  }

  const tieneVariantes = tieneFeature(empresa, 'variantes');
  const tieneCupones = tieneFeature(empresa, 'cupones');
  const modulos = [...MODULOS_BASE];
  modulos.splice(2, 0, {
    ruta: '/variantes',
    etiqueta: 'Variantes',
    icono: iconoVariantes,
    bloqueado: !tieneVariantes,
  });
  modulos.splice(3, 0, {
    ruta: '/cupones',
    etiqueta: 'Cupones',
    icono: iconoCupones,
    bloqueado: !tieneCupones,
  });
  return modulos;
}

/**
 * @param {HTMLElement} contenedorRaiz
 * @param {string} rutaActiva
 * @returns {HTMLElement} el nodo donde la vista debe montar su contenido.
 */
export function montarShell(contenedorRaiz, rutaActiva) {
  const { sesion } = obtenerEstadoSesion();
  const correo = sesion?.user?.email ?? '';

  contenedorRaiz.innerHTML = `
    <div class="pf-shell" id="pf-shell">
      <div class="pf-shell__backdrop" id="pf-sidebar-backdrop"></div>
      <aside class="pf-shell__sidebar" id="pf-sidebar">
        <div class="pf-shell__marca">
          <img src="/logo.png" alt="Vimos Digital" class="pf-shell__marca-logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
          <div class="pf-shell__marca-texto pf-shell__marca-texto--fallback" style="display:none;">
            <span class="pf-shell__marca-nombre">Vimos Digital</span>
            <span>Panel de tienda</span>
          </div>
        </div>
        <nav class="pf-shell__nav">
          ${modulosDisponibles()
            .map((m) => {
              if (m.bloqueado) {
                return `
                <span class="pf-shell__enlace--bloqueado" title="Disponible desde un plan superior">
                  <span class="pf-shell__enlace-icono">${m.icono()}</span>
                  <span class="pf-shell__enlace-texto">${m.etiqueta}</span>
                  <span class="pf-shell__enlace-candado">${iconoCandado()}</span>
                </span>`;
              }
              return `
              <a href="#${m.ruta}" class="pf-shell__enlace${m.ruta === rutaActiva ? ' pf-shell__enlace--activo' : ''}">
                <span class="pf-shell__enlace-icono">${m.icono()}</span>
                <span class="pf-shell__enlace-texto">${m.etiqueta}</span>
              </a>`;
            })
            .join('')}
        </nav>
      </aside>
      <div class="pf-shell__principal">
        <header class="pf-shell__topbar">
          <div class="pf-shell__topbar-izquierda">
            <button type="button" class="pf-shell__hamburger" id="pf-hamburger" aria-label="Abrir menú" aria-expanded="false">
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          <div class="pf-shell__topbar-derecha">
            <span class="pf-shell__usuario">
              <span class="pf-shell__usuario-icono">${iconoUsuario()}</span>
              ${correo}
            </span>
            <a href="#/cambiar-contrasena" class="pf-boton pf-boton--secundario pf-boton--chico">
              Cambiar contraseña
            </a>
            <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="pf-boton-salir">
              Cerrar sesión
            </button>
          </div>
        </header>
        <main class="pf-shell__contenido"></main>
      </div>
    </div>
  `;

  const sidebar = contenedorRaiz.querySelector('#pf-sidebar');
  const backdrop = contenedorRaiz.querySelector('#pf-sidebar-backdrop');
  const hamburger = contenedorRaiz.querySelector('#pf-hamburger');

  function abrirMenu() {
    sidebar.classList.add('pf-shell__sidebar--abierto');
    backdrop.classList.add('pf-shell__backdrop--visible');
    hamburger.classList.add('pf-shell__hamburger--activo');
    hamburger.setAttribute('aria-expanded', 'true');
    hamburger.setAttribute('aria-label', 'Cerrar menú');
  }

  function cerrarMenu() {
    sidebar.classList.remove('pf-shell__sidebar--abierto');
    backdrop.classList.remove('pf-shell__backdrop--visible');
    hamburger.classList.remove('pf-shell__hamburger--activo');
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-label', 'Abrir menú');
  }

  function toggleMenu() {
    if (sidebar.classList.contains('pf-shell__sidebar--abierto')) {
      cerrarMenu();
    } else {
      abrirMenu();
    }
  }

  hamburger.addEventListener('click', toggleMenu);
  backdrop.addEventListener('click', cerrarMenu);

  // Cerrar el menú al navegar en móvil (al seleccionar un enlace)
  sidebar.querySelectorAll('.pf-shell__enlace').forEach((enlace) => {
    enlace.addEventListener('click', () => {
      if (window.innerWidth <= 720) {
        cerrarMenu();
      }
    });
  });

  // Cerrar con Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar.classList.contains('pf-shell__sidebar--abierto')) {
      cerrarMenu();
    }
  });

  contenedorRaiz.querySelector('#pf-boton-salir').addEventListener('click', async () => {
    await cerrarSesion();
  });

  return contenedorRaiz.querySelector('.pf-shell__contenido');
}
